/**
 * API Error Handling Utilities
 *
 * Comprehensive error handling for WordPress REST API calls
 * Provides custom error types, parsing, and user-friendly messaging
 *
 * @package WooAIChatbot
 * @author Roman Siromskyi <leowebmark@gmail.com>
 */
/**
 * Error type enumeration for categorizing API errors
 */
export var ErrorType;
(function (ErrorType) {
    /** Network connectivity issues */
    ErrorType["Network"] = "NETWORK_ERROR";
    /** Authentication/authorization failures */
    ErrorType["Auth"] = "AUTH_ERROR";
    /** Validation errors from server */
    ErrorType["Validation"] = "VALIDATION_ERROR";
    /** Server-side errors (5xx) */
    ErrorType["Server"] = "SERVER_ERROR";
    /** Unknown or uncategorized errors */
    ErrorType["Unknown"] = "UNKNOWN_ERROR";
})(ErrorType || (ErrorType = {}));
/**
 * Custom API Error class with enhanced error handling capabilities
 */
export class ApiError extends Error {
    /**
     * Creates a new ApiError instance
     *
     * @param message - Human-readable error message
     * @param type - Categorized error type
     * @param statusCode - HTTP status code (optional)
     * @param details - Additional error details (optional)
     * @param originalError - Original error object (optional)
     */
    constructor(message, type = ErrorType.Unknown, statusCode, details, originalError) {
        super(message);
        /** Error type for categorization */
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** HTTP status code if available */
        Object.defineProperty(this, "statusCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Additional error details from the server */
        Object.defineProperty(this, "details", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Original error that caused this error */
        Object.defineProperty(this, "originalError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Timestamp when error occurred */
        Object.defineProperty(this, "timestamp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.name = 'ApiError';
        this.type = type;
        this.statusCode = statusCode;
        this.details = details;
        this.originalError = originalError;
        this.timestamp = new Date();
        // Maintains proper stack trace for where error was thrown (V8 only)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiError);
        }
        // Set prototype explicitly to maintain instanceof checks
        Object.setPrototypeOf(this, ApiError.prototype);
    }
    /**
     * Get user-friendly error message
     * Translates technical errors into understandable messages
     *
     * @returns User-friendly error message
     */
    getUserMessage() {
        // Use custom message if it's already user-friendly
        if (this.isUserFriendly(this.message)) {
            return this.message;
        }
        // Generate user-friendly message based on error type
        switch (this.type) {
            case ErrorType.Network:
                return 'Unable to connect to the server. Please check your internet connection and try again.';
            case ErrorType.Auth:
                if (this.statusCode === 401) {
                    return 'Your session has expired. Please refresh the page and try again.';
                }
                if (this.statusCode === 403) {
                    return 'You don\'t have permission to perform this action.';
                }
                return 'Authentication failed. Please log in again.';
            case ErrorType.Validation:
                return this.message || 'The information provided is invalid. Please check your input and try again.';
            case ErrorType.Server:
                return 'A server error occurred. Please try again later.';
            case ErrorType.Unknown:
            default:
                return 'An unexpected error occurred. Please try again.';
        }
    }
    /**
     * Determine if error should be retried
     *
     * @returns true if the error is retryable
     */
    shouldRetry() {
        // Network errors are retryable
        if (this.type === ErrorType.Network) {
            return true;
        }
        // Server errors (5xx) are retryable
        if (this.type === ErrorType.Server) {
            return true;
        }
        // 429 Too Many Requests is retryable
        if (this.statusCode === 429) {
            return true;
        }
        // 408 Request Timeout is retryable
        if (this.statusCode === 408) {
            return true;
        }
        // 503 Service Unavailable is retryable
        if (this.statusCode === 503) {
            return true;
        }
        // All other errors are not retryable
        return false;
    }
    /**
     * Get retry delay in milliseconds
     * Implements exponential backoff for retries
     *
     * @param attemptNumber - Current retry attempt number (0-based)
     * @returns Delay in milliseconds before next retry
     */
    getRetryDelay(attemptNumber = 0) {
        // Check if Retry-After header is available in details
        if (this.details && typeof this.details === 'object' && 'retryAfter' in this.details) {
            const retryAfter = this.details.retryAfter;
            if (typeof retryAfter === 'number' && retryAfter > 0) {
                return retryAfter * 1000; // Convert seconds to milliseconds
            }
        }
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s (max)
        const baseDelay = 1000;
        const maxDelay = 16000;
        const delay = Math.min(baseDelay * Math.pow(2, attemptNumber), maxDelay);
        // Add jitter to prevent thundering herd
        const jitter = Math.random() * 0.3 * delay; // ±30% jitter
        return Math.floor(delay + jitter);
    }
    /**
     * Check if message is already user-friendly
     */
    isUserFriendly(message) {
        // Check if message contains technical terms
        const technicalTerms = [
            'undefined',
            'null',
            'NaN',
            'TypeError',
            'ReferenceError',
            'fetch',
            'XMLHttpRequest',
            'CORS',
            'JSON',
            'parse',
        ];
        return !technicalTerms.some(term => message.toLowerCase().includes(term.toLowerCase()));
    }
    /**
     * Serialize error for logging
     */
    toJSON() {
        return {
            name: this.name,
            message: this.message,
            type: this.type,
            statusCode: this.statusCode,
            details: this.details,
            timestamp: this.timestamp.toISOString(),
            stack: this.stack,
        };
    }
}
/**
 * Parse and categorize API errors
 * Handles WordPress REST API error responses and generic errors
 *
 * @param error - Error to parse (can be any type)
 * @returns Structured ApiError instance
 */
export function parseApiError(error) {
    // Already an ApiError, return as-is
    if (error instanceof ApiError) {
        return error;
    }
    // Handle Response objects from fetch (async operation needed)
    if (error instanceof Response) {
        // For Response objects, caller should await parseResponseError directly
        return new ApiError('Response parsing requires async handling', ErrorType.Unknown, error.status);
    }
    // Handle standard Error objects
    if (error instanceof Error) {
        return parseStandardError(error);
    }
    // Handle WordPress REST API error format
    if (isWPRestError(error)) {
        return parseWPRestError(error);
    }
    // Handle plain objects with error information
    if (typeof error === 'object' && error !== null) {
        return parseObjectError(error);
    }
    // Handle string errors
    if (typeof error === 'string') {
        return new ApiError(error, ErrorType.Unknown);
    }
    // Unknown error type
    return new ApiError('An unknown error occurred', ErrorType.Unknown, undefined, error);
}
/**
 * Parse Response object errors (async)
 * Export for use in fetch error handling
 */
export async function parseResponseError(response) {
    const statusCode = response.status;
    let errorData;
    try {
        errorData = await response.json();
    }
    catch {
        try {
            errorData = await response.text();
        }
        catch {
            errorData = 'Failed to read error response';
        }
    }
    // Determine error type from status code
    const type = categorizeStatusCode(statusCode);
    // Extract message from error data
    let message = 'Request failed';
    if (typeof errorData === 'object' && errorData !== null) {
        if ('message' in errorData && typeof errorData.message === 'string') {
            message = errorData.message;
        }
    }
    else if (typeof errorData === 'string') {
        message = errorData;
    }
    return new ApiError(message, type, statusCode, errorData);
}
/**
 * Parse standard Error objects
 */
function parseStandardError(error) {
    // Network errors
    if (error.message.includes('fetch') ||
        error.message.includes('network') ||
        error.message.includes('Failed to fetch')) {
        return new ApiError(error.message, ErrorType.Network, undefined, undefined, error);
    }
    // Default to unknown error
    return new ApiError(error.message, ErrorType.Unknown, undefined, undefined, error);
}
/**
 * Parse WordPress REST API error format
 */
function parseWPRestError(error) {
    const statusCode = error.data?.status;
    const type = categorizeWPErrorCode(error.code, statusCode);
    return new ApiError(error.message, type, statusCode, error.data);
}
/**
 * Parse generic object errors
 */
function parseObjectError(error) {
    const message = typeof error.message === 'string'
        ? error.message
        : 'An error occurred';
    const statusCode = typeof error.statusCode === 'number'
        ? error.statusCode
        : typeof error.status === 'number'
            ? error.status
            : undefined;
    const type = statusCode
        ? categorizeStatusCode(statusCode)
        : ErrorType.Unknown;
    return new ApiError(message, type, statusCode, error);
}
/**
 * Type guard for WordPress REST API errors
 */
function isWPRestError(error) {
    return (typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        'message' in error &&
        typeof error.code === 'string' &&
        typeof error.message === 'string');
}
/**
 * Categorize error based on HTTP status code
 */
function categorizeStatusCode(statusCode) {
    // Network/timeout errors
    if (statusCode === 0 || statusCode === 408 || statusCode === 504) {
        return ErrorType.Network;
    }
    // Authentication/authorization errors
    if (statusCode === 401 || statusCode === 403) {
        return ErrorType.Auth;
    }
    // Validation errors
    if (statusCode >= 400 && statusCode < 500) {
        return ErrorType.Validation;
    }
    // Server errors
    if (statusCode >= 500) {
        return ErrorType.Server;
    }
    return ErrorType.Unknown;
}
/**
 * Categorize error based on WordPress error code
 */
function categorizeWPErrorCode(code, statusCode) {
    // Use status code if available
    if (statusCode) {
        return categorizeStatusCode(statusCode);
    }
    // WordPress-specific error codes
    const authCodes = [
        'rest_forbidden',
        'rest_not_logged_in',
        'rest_cookie_invalid_nonce',
        'jwt_auth_invalid_token',
        'jwt_auth_no_auth_header',
    ];
    const validationCodes = [
        'rest_invalid_param',
        'rest_missing_callback_param',
        'rest_additional_properties_forbidden',
    ];
    if (authCodes.includes(code)) {
        return ErrorType.Auth;
    }
    if (validationCodes.includes(code)) {
        return ErrorType.Validation;
    }
    return ErrorType.Unknown;
}
/**
 * Log error to console and optionally to server
 *
 * @param error - ApiError to log
 * @param context - Additional context for the error
 */
export function logError(error, context) {
    // Prepare error log data
    const logData = {
        ...error.toJSON(),
        context,
        userAgent: navigator.userAgent,
        url: window.location.href,
    };
    // Log to console (intentional for error reporting)
    /* eslint-disable no-console */
    if (process.env.NODE_ENV === 'development') {
        console.group(`🔴 API Error: ${error.type}`);
        console.error('Message:', error.message);
        console.error('Type:', error.type);
        if (error.statusCode) {
            console.error('Status Code:', error.statusCode);
        }
        if (error.details) {
            console.error('Details:', error.details);
        }
        if (context) {
            console.error('Context:', context);
        }
        if (error.stack) {
            console.error('Stack:', error.stack);
        }
        console.groupEnd();
    }
    else {
        // Production: log minimal info
        console.error(`API Error (${error.type}):`, error.getUserMessage());
    }
    /* eslint-enable no-console */
    // Send critical errors to server for monitoring
    if (shouldReportToServer(error)) {
        reportErrorToServer(logData);
    }
}
/**
 * Determine if error should be reported to server
 */
function shouldReportToServer(error) {
    // Don't report validation errors (user input issues)
    if (error.type === ErrorType.Validation) {
        return false;
    }
    // Don't report auth errors (expected user behavior)
    if (error.type === ErrorType.Auth) {
        return false;
    }
    // Report network, server, and unknown errors
    return true;
}
/**
 * Report error to server for monitoring
 * Uses navigator.sendBeacon for reliability
 */
function reportErrorToServer(logData) {
    try {
        const endpoint = '/wp-json/woo-ai/v1/errors';
        const data = JSON.stringify(logData);
        // Use sendBeacon if available (more reliable for errors)
        if (navigator.sendBeacon) {
            const blob = new Blob([data], { type: 'application/json' });
            navigator.sendBeacon(endpoint, blob);
        }
        else {
            // Fallback to fetch with keepalive
            fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: data,
                keepalive: true,
            }).catch(() => {
                // Silently fail - error reporting shouldn't break the app
            });
        }
    }
    catch {
        // Silently fail - error reporting shouldn't break the app
    }
}
/**
 * Helper function to check if error is retryable
 *
 * @param error - Error to check
 * @returns true if error should be retried
 */
export function isRetryableError(error) {
    const apiError = parseApiError(error);
    return apiError.shouldRetry();
}
/**
 * Helper function to get user-friendly error message
 *
 * @param error - Error to get message from
 * @returns User-friendly error message
 */
export function getUserErrorMessage(error) {
    const apiError = parseApiError(error);
    return apiError.getUserMessage();
}
/**
 * Create a validation error
 *
 * @param message - Error message
 * @param details - Validation error details
 * @returns ApiError with validation type
 */
export function createValidationError(message, details) {
    return new ApiError(message, ErrorType.Validation, 400, details);
}
/**
 * Create a network error
 *
 * @param message - Error message
 * @returns ApiError with network type
 */
export function createNetworkError(message) {
    return new ApiError(message || 'Network request failed', ErrorType.Network);
}
/**
 * Create an auth error
 *
 * @param message - Error message
 * @param statusCode - HTTP status code (401 or 403)
 * @returns ApiError with auth type
 */
export function createAuthError(message, statusCode) {
    return new ApiError(message || 'Authentication required', ErrorType.Auth, statusCode || 401);
}
